import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Iconify from 'src/components/iconify';

export default function CommonFilterResult({
  filters,
  onFilters,
  filterSections,
  canReset,
  onResetFilters,
  results,
  ...other
}) {
  // Remove filter value
  const handleRemoveFilter = (filterKey, inputValue) => {
    const newValue = filters[filterKey].filter((item) => item !== inputValue);
    onFilters(filterKey, newValue);
  };

  // Format value safely
  const formatValue = (value) => {
    if (value instanceof Date) {
      return new Date(value).toLocaleDateString('en-GB');
    }
    return value;
  };

  return (
    <Stack spacing={1.5} {...other}>
      {/* Result Count */}
      <Box sx={{ typography: 'body2' }}>
        <strong>{results}</strong>
        <Box component="span" sx={{ color: 'text.secondary', ml: 0.25 }}>
          records found
        </Box>
      </Box>

      <Stack flexGrow={1} spacing={1} direction="row" flexWrap="wrap" alignItems="center">
        {/*  NORMAL FILTERS */}
        {filterSections.map(({ title, filterKey }) => {
          //  Skip date filters
          if (filterKey === 'StartDate' || filterKey === 'EndDate') {
            return null;
          }

          if (filters[filterKey]?.length > 0) {
            return (
              <Block key={filterKey} label={`${title}:`}>
                {filters[filterKey].map((item, index) => (
                  <Chip
                    key={index}
                    label={formatValue(item)}
                    size="small"
                    onDelete={() => handleRemoveFilter(filterKey, item)}
                  />
                ))}
              </Block>
            );
          }
          return null;
        })}

        {/*  START DATE RANGE */}
        {filters.StartDate?.[0] && filters.StartDate?.[1] && (
          <Block label="Start Date:">
            <Chip
              label={`${new Date(filters.StartDate[0]).toLocaleDateString('en-GB')} → ${new Date(
                filters.StartDate[1]
              ).toLocaleDateString('en-GB')}`}
              size="small"
              onDelete={() => onFilters('StartDate', [])}
            />
          </Block>
        )}

        {/*  END DATE RANGE */}
        {filters.EndDate?.[0] && filters.EndDate?.[1] && (
          <Block label="End Date:">
            <Chip
              label={`${new Date(filters.EndDate[0]).toLocaleDateString('en-GB')} → ${new Date(
                filters.EndDate[1]
              ).toLocaleDateString('en-GB')}`}
              size="small"
              onDelete={() => onFilters('EndDate', [])}
            />
          </Block>
        )}

        {/* CLEAR BUTTON */}
        {canReset && (
          <Button
            color="error"
            onClick={onResetFilters}
            startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
          >
            Clear
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

// ======================================================

CommonFilterResult.propTypes = {
  canReset: PropTypes.bool,
  filters: PropTypes.object,
  filterSections: PropTypes.array,
  onFilters: PropTypes.func,
  onResetFilters: PropTypes.func,
  results: PropTypes.number,
};

// ======================================================

function Block({ label, children, sx, ...other }) {
  return (
    <Stack
      component={Paper}
      variant="outlined"
      spacing={1}
      direction="row"
      sx={{
        p: 1,
        borderRadius: 1,
        overflow: 'hidden',
        borderStyle: 'dashed',
        ...sx,
      }}
      {...other}
    >
      <Box component="span" sx={{ typography: 'subtitle2' }}>
        {label}
      </Box>

      <Stack spacing={1} direction="row" flexWrap="wrap">
        {children}
      </Stack>
    </Stack>
  );
}

Block.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string,
  sx: PropTypes.object,
};
